// Types for products and packaging

// Product or box dimensions
export interface Dimensions {
  length: number;  // Length
  width: number;   // Width
  height: number;  // Height
}

// Measurement units
export type MeasurementUnit = 'in' | 'cm';

// Rotation options
export type RotationOption = 'length' | 'width' | 'flipped';

// Represents a product to be packaged
export interface Product {
  id: string;
  quantity: number;
  dimensions: Dimensions;
  unit: MeasurementUnit;
  rotationOptions: RotationOption[];  // Multiple rotation options can be selected
}

// Represents a multi-product packaging request
export interface MultiProductRequest {
  products: Product[];
  // Common properties for all products in the request
  commonUnit?: MeasurementUnit; // If specified, used as default unit for display
}

// Represents a packaging box
export interface Box {
  id: string;
  name: string;
  dimensions: Dimensions;
  unit?: MeasurementUnit; // Added unit property for boxes
  visible?: boolean; // Whether this box should be included in packaging results
  isExample?: boolean; // Boîte d'exemple seedée automatiquement (mode démo) — retirée dès qu'une vraie boîte existe
}

// Position information for product placement in 3D space
export interface Position {
  x: number;
  y: number;
  z: number;
}

// Information about product placement in a box
export interface ProductPlacement {
  productIndex: number; // Index of the product in the products array
  dimensions: Dimensions; // Dimensions after any rotation
  position: Position; // Position in 3D space (center of the product)
}

// Information about placements in a specific box instance
export interface BoxInstance {
  instanceIndex: number; // Index of this box instance (0-based)
  productPlacements: ProductPlacement[]; // Placements for this specific box instance
}

// The result of a product-box compatibility analysis
export interface PackagingSolution {
  boxId: string;
  boxName: string;
  // Box dimensions carried in the solution so shared links can render the 3D
  // view without access to the sender's local box database.
  boxDimensions?: Dimensions;
  boxUnit?: MeasurementUnit;
  productsPerBox: number;
  totalBoxesNeeded: number;
  orientation: 'standard' | 'tilted' | 'length-rotated' | 'width-rotated' | 'flipped' | 'combined-rotation';
  spaceUtilization: number; // Space utilization percentage (0-100)
  visualRepresentation?: string; // URL or data for 3D representation
  // For multi-product solutions
  products?: Product[]; // The products included in this solution
  totalProductsQuantity?: number; // Total number of products in this solution
  // Prix optionnel pour l'affichage client
  price?: string; // Optional price for client display
  // Information about product placement for visualization
  productPlacements?: ProductPlacement[]; // Positions of products in the first box (backward compatibility)
  // NEW: Array of box instances with their specific placements
  boxInstances?: BoxInstance[]; // Placements for each box instance
}

// Color configuration — "Load Deck" visual identity (shipping-operations console).
// primary/secondary keys are kept (widely imported) but re-valued to the new palette;
// the semantic tokens below are the source of truth for new UI.
export const BRAND_COLORS = {
  // Action colors (backward-compatible keys)
  primary: '#103A63', // Dock Blue — actions, header, headings
  secondary: '#0F8A47', // Signal Green — fit / confirm
  // Neutrals (legacy)
  lightGray: '#EDEDED',
  mediumGray: '#C6C6C6',
  // Load Deck semantic tokens
  deck: '#E6E9ED', // operational deck — app background + 3D canvas clear color
  ink: '#17222E', // primary text + 1.5px structural borders + ruler ticks
  dockBlue: '#103A63', // = primary
  green: '#0F8A47', // vivid fill signal (bars, markers, voxels)
  greenText: '#0B7A3E', // AA-safe green for text / numerals
  amber: '#E08A16', // mid-fill bar
  amberText: '#A85E00', // AA-safe amber for text / numerals
  red: '#C33A2E', // low-fill bar
  redText: '#A32B20', // AA-safe red for text / numerals
  cyan: '#00A6ED', // measurement accent — GRAPHIC ONLY, never text
  kraft: '#C6924E', // material stamp — fill under dark ink only, never text on white
  cardStock: '#FDFEFF', // card / panel surface
  slate: '#8CA0B3', // muted meta / headroom / void — GRAPHIC ONLY (fails AA as text)
  slateText: '#64748B', // AA-safe slate for small text (labels, overlines, keys)
  slateSoft: '#DCE2E9', // empty gauge track
};

// Typography stacks for the Load Deck identity.
export const FONTS = {
  display: '"Saira Semi Condensed", system-ui, -apple-system, sans-serif', // labels, headings, stamps
  body: '"IBM Plex Sans", system-ui, -apple-system, sans-serif', // body copy
  mono: '"IBM Plex Mono", ui-monospace, "SFMono-Regular", Menlo, monospace', // ALL numbers
}; 